<template>
  <v-row no-gutters justify="center" style="text-align: left">
    <v-dialog v-model="dialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <template v-for="(_, scopedSlotName) in $scopedSlots" v-slot:[scopedSlotName]="slotData">
        <slot :name="scopedSlotName" v-bind="slotData" />
      </template>

      <v-card class="d-flex flex-column">
        <v-toolbar color="primary" class="shrink">
          <v-btn icon @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>
            {{ $t('Horoscope Book') }} · #{{ order.orderNumber }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn
              v-if="$listeners['download-zip']"
              text
              :disabled="busy"
              @click="$emit('download-zip', order)"
            >
              <v-icon left>
                {{ track === 'digital' ? 'mdi-file-pdf-box' : 'mdi-folder-zip-outline' }}
              </v-icon>
              {{ $t(fallbackLabel) }}
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>
        <v-progress-linear
          :active="loading"
          :indeterminate="loading"
          top
          color="white"
        ></v-progress-linear>

        <v-row no-gutters class="pa-5 grow">
          <!-- Left: what staff need to generate the text -->
          <v-col cols="4" class="pr-5 text-left info-column">
            <v-list dense>
              <v-subheader>{{ $t('Customer') }}</v-subheader>
              <v-list-item v-for="field in customerFields" :key="field.label" dense>
                <v-list-item-content>
                  <v-list-item-subtitle>{{ $t(field.label) }}</v-list-item-subtitle>
                  <v-list-item-title class="text-wrap">{{ field.value }}</v-list-item-title>
                </v-list-item-content>
                <v-list-item-action class="ml-4">
                  <v-btn icon x-small :title="$t('Copy')" @click="copy(field.value)">
                    <v-icon small>mdi-content-copy</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>

            <v-alert v-if="loadError" type="warning" dense text class="mx-4">
              {{ $t('Could not load horoscope details') }}
            </v-alert>

            <v-list v-for="book in books" :key="`info-${book.lineItemId}`" dense>
              <v-subheader>
                {{ $t('Horoscope Info') }}
                <v-chip v-if="book.quantity > 1" x-small class="ml-2">× {{ book.quantity }}</v-chip>
                <v-chip v-if="book.editedBy" x-small class="ml-2" color="warning" outlined>
                  {{ $t('edited') }} · {{ book.editedBy }}
                </v-chip>
              </v-subheader>

              <!-- Each row: value + copy, or an inline editor. Corrections live on the
                   book (never on the order) and apply at the next assembly. -->
              <v-list-item v-for="field in infoFields(book)" :key="field.key" dense>
                <v-list-item-content v-if="book.editing !== field.key">
                  <v-list-item-subtitle>
                    {{ $t(field.label) }}
                    <span v-if="field.edited" class="warning--text ml-1">· {{ $t('edited') }}</span>
                    <span v-if="field.noCoordinates" class="error--text ml-1">· {{ $t('no coordinates') }}</span>
                  </v-list-item-subtitle>
                  <v-list-item-title class="text-wrap">
                    {{ field.value || '—' }}
                  </v-list-item-title>
                </v-list-item-content>
                <v-list-item-content v-else class="pt-1">
                  <v-list-item-subtitle class="mb-1">{{ $t(field.label) }}</v-list-item-subtitle>
                  <v-select
                    v-if="field.type === 'design'"
                    v-model="book.draft.value"
                    :items="designs"
                    dense
                    outlined
                    hide-details
                  ></v-select>
                  <v-select
                    v-else-if="field.type === 'book-type'"
                    v-model="book.draft.value"
                    :items="bookTypeItems"
                    dense
                    outlined
                    hide-details
                  ></v-select>
                  <v-select
                    v-else-if="field.type === 'language'"
                    v-model="book.draft.value"
                    :items="languageItems(book)"
                    dense
                    outlined
                    hide-details
                  ></v-select>
                  <v-select
                    v-else-if="field.type === 'gender'"
                    v-model="book.draft.value"
                    :items="genderItems"
                    dense
                    outlined
                    hide-details
                  ></v-select>
                  <div v-else-if="field.type === 'date'" class="d-flex align-center">
                    <v-text-field
                      v-model="book.draft.date"
                      dense
                      outlined
                      hide-details
                      placeholder="DD.MM.YYYY"
                      class="mr-2"
                      style="max-width: 130px"
                    ></v-text-field>
                    <v-text-field
                      v-model="book.draft.time"
                      dense
                      outlined
                      hide-details
                      placeholder="HH:mm"
                      style="max-width: 90px"
                    ></v-text-field>
                    <v-checkbox
                      v-model="book.draft.showTime"
                      dense
                      hide-details
                      class="ml-2 mt-0"
                      :label="$t('show time')"
                    ></v-checkbox>
                  </div>
                  <birth-place-field
                    v-else-if="field.type === 'place'"
                    v-model="book.draft.place"
                    hide-details
                  ></birth-place-field>
                  <v-textarea
                    v-else-if="field.type === 'text-long'"
                    v-model="book.draft.value"
                    dense
                    outlined
                    hide-details
                    rows="3"
                    auto-grow
                  ></v-textarea>
                  <v-text-field
                    v-else
                    v-model="book.draft.value"
                    dense
                    outlined
                    hide-details
                  ></v-text-field>
                  <div class="mt-2">
                    <v-btn small color="primary" :loading="book.saving" @click="saveEdit(book, field)">
                      {{ $t('Save') }}
                    </v-btn>
                    <v-btn small text @click="book.editing = null">{{ $t('Cancel') }}</v-btn>
                  </div>
                </v-list-item-content>
                <v-list-item-action v-if="book.editing !== field.key" class="ml-4 flex-row">
                  <v-btn
                    v-if="field.value"
                    icon
                    x-small
                    :title="$t('Copy')"
                    @click="copy(field.value)"
                  >
                    <v-icon small>mdi-content-copy</v-icon>
                  </v-btn>
                  <v-btn
                    v-if="field.edited"
                    icon
                    x-small
                    :title="$t('Revert to the order')"
                    :disabled="busy"
                    @click="revertEdit(book, field)"
                  >
                    <v-icon small>mdi-undo-variant</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    x-small
                    :title="$t('Edit')"
                    :disabled="busy"
                    @click="startEdit(book, field)"
                  >
                    <v-icon small>mdi-pencil-outline</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-col>

          <!-- Right: one card per book -->
          <v-col cols="8">
            <v-card
              v-for="book in books"
              :key="`book-${book.lineItemId}`"
              outlined
              class="mb-5 text-left"
            >
              <v-card-title class="subtitle-1">
                {{ book.title }}
                <v-spacer></v-spacer>
                <!-- A book is digital only (basic) or digital + print (plus): pick the track. -->
                <v-btn-toggle
                  v-if="book.tracks.length > 1"
                  v-model="book.track"
                  mandatory
                  dense
                  class="mr-3"
                  @change="onTrackChange(book)"
                >
                  <v-btn value="print" small text>
                    <v-icon small left>mdi-printer-wireless</v-icon>{{ $t('Print') }}
                  </v-btn>
                  <v-btn value="digital" small text>
                    <v-icon small left>mdi-email-fast-outline</v-icon>{{ $t('Digital') }}
                  </v-btn>
                </v-btn-toggle>
                <v-chip small :color="stageColor(book)" dark>{{ $t(stageLabel(book)) }}</v-chip>
              </v-card-title>

              <!-- Generated text (gravur); a staff upload in step 1 always wins. -->
              <horoscope-generation-panel
                :book="book"
                :order-id="order.id"
                :store-id="store.id"
                :disabled="busy"
                @view="(view) => onGenerationView(book, view)"
                @notify="notify"
                @refresh="poll"
              />

              <v-stepper
                :value="book.step"
                vertical
                flat
                class="elevation-0"
                @change="(step) => (book.step = step)"
              >
                <!-- 1. Text PDF -->
                <v-stepper-step :complete="!!book.uploaded" :editable="!!book.uploaded" step="1">
                  {{ $t('Text PDF') }}
                  <small v-if="book.uploaded">
                    {{
                      book.uploaded.source === 'gravur'
                        ? $t('Generated text (gravur)')
                        : `${book.uploaded.filename} · ${$t('staff upload')}`
                    }}
                  </small>
                </v-stepper-step>
                <v-stepper-content step="1">
                  <v-alert v-if="isAtCpi(book)" type="warning" dense text class="mb-3">
                    {{ $t('Already ordered at CPI. Changes here do not change that order — to print the changed book, assemble it again and submit to CPI again.') }}
                  </v-alert>
                  <div v-if="book.uploaded && book.uploaded.source === 'gravur'" class="text--secondary mb-2">
                    {{ $t('The generated text is in use. Uploading a PDF replaces it.') }}
                  </div>
                  <div
                    class="book-dropzone d-flex align-center"
                    :class="{ 'book-dropzone--over': book.dragOver }"
                    @click="openFileDialog(book)"
                    @dragover.prevent="book.dragOver = true"
                    @dragleave.prevent="book.dragOver = false"
                    @drop.prevent="onDrop(book, $event)"
                  >
                    <input
                      :ref="`file-${book.lineItemId}`"
                      type="file"
                      accept="application/pdf,.pdf"
                      class="d-none"
                      @change="onFileInput(book, $event)"
                    />
                    <template v-if="book.file">
                      <div class="book-thumb mr-4">
                        <img v-if="book.thumb" :src="book.thumb" alt="" />
                        <v-progress-circular v-else indeterminate size="20" width="2" />
                      </div>
                      <div class="grow">
                        <div class="font-weight-medium">{{ book.file.name }}</div>
                        <div class="text--secondary">
                          {{ book.pageCount }} {{ $tc('page', book.pageCount) }} ·
                          {{ formatSize(book.file.size) }}
                        </div>
                      </div>
                    </template>
                    <template v-else>
                      <v-icon large color="grey" class="mr-4">mdi-file-pdf-box</v-icon>
                      <div class="text--secondary">
                        {{ $t('Drop the generated text PDF here or click to choose') }}
                      </div>
                    </template>
                  </div>
                  <v-btn
                    color="primary"
                    class="mt-3"
                    :disabled="!book.file || busy"
                    :loading="book.uploading"
                    @click="upload(book)"
                  >
                    <v-icon left>mdi-cloud-upload-outline</v-icon>
                    {{ $t('Upload') }}
                  </v-btn>
                </v-stepper-content>

                <!-- 2. Assemble -->
                <v-stepper-step :complete="!!book.assembled" :editable="!!book.uploaded" step="2">
                  {{ $t(book.track === 'digital' ? 'Assemble digital book' : 'Assemble book') }}
                  <small>
                    {{
                      $t(
                        book.track === 'digital'
                          ? 'Cover, wheel chart, greeting and text as one PDF'
                          : 'Cover, wheel chart, greeting, text, QR and the CPI XML'
                      )
                    }}
                  </small>
                </v-stepper-step>
                <v-stepper-content step="2">
                  <v-alert v-if="isAtCpi(book)" type="warning" dense text class="mb-3">
                    {{ $t('Already ordered at CPI. Changes here do not change that order — to print the changed book, assemble it again and submit to CPI again.') }}
                  </v-alert>
                  <v-btn
                    color="primary"
                    :disabled="!book.uploaded || busy"
                    :loading="book.assembling"
                    @click="assemble(book)"
                  >
                    <v-icon left>mdi-book-open-page-variant-outline</v-icon>
                    {{ book.assembled ? $t('Assemble again') : $t('Assemble') }}
                  </v-btn>
                </v-stepper-content>

                <!-- 3. Review & submit (print) / review & send (digital) -->
                <v-stepper-step :complete="isDelivered(book)" :editable="!!book.assembled" step="3">
                  {{ $t(book.track === 'digital' ? 'Review & send to customer' : 'Review & submit to CPI') }}
                  <small>
                    {{
                      $t(
                        book.track === 'digital'
                          ? 'The PDF exactly as the customer receives it'
                          : 'The four files exactly as CPI receives them'
                      )
                    }}
                  </small>
                </v-stepper-step>
                <!-- One content element per step: swapping two with v-if/v-else lets
                     Vue reuse the instance and leak the other track's panel. -->
                <v-stepper-content step="3">
                  <template v-if="book.track === 'digital'">
                  <!-- Digital: one PDF, then the delivery email -->
                  <div v-if="book.assembled && book.assembled.combined" class="mb-4 review-pane">
                    <div class="d-flex align-center px-2 py-1 text--secondary review-bar">
                      <span class="text-truncate">{{ book.assembled.combined.filename }}</span>
                      <v-spacer></v-spacer>
                      <v-btn
                        icon
                        small
                        :href="book.assembled.combined.url"
                        target="_blank"
                        :title="$t('Open in new tab')"
                      >
                        <v-icon small>mdi-open-in-new</v-icon>
                      </v-btn>
                    </div>
                    <iframe
                      :src="reviewUrl(book, book.assembled.combined)"
                      class="review-frame"
                      :title="book.assembled.combined.filename"
                    ></iframe>
                  </div>
                  <v-alert v-if="book.delivery" type="success" dense text>
                    {{ $t('Sent to') }} {{ book.delivery.to }}
                  </v-alert>
                  <div class="d-flex align-center">
                    <v-text-field
                      v-model="book.emailTo"
                      dense
                      outlined
                      hide-details
                      :label="$t('To:')"
                      class="mr-3"
                      style="max-width: 360px"
                    ></v-text-field>
                    <v-btn
                      color="primary"
                      :disabled="!book.assembled || busy || !isValidEmail(book.emailTo)"
                      :loading="book.sending"
                      @click="send(book)"
                    >
                      <v-icon left>mdi-email-fast-outline</v-icon>
                      {{ book.delivery ? $t('Send again') : $t('Send to customer') }}
                    </v-btn>
                  </div>
                  </template>
                  <template v-else>
                  <!-- Review: the four files CPI will receive, as they are -->
                  <div v-if="book.assembled" class="mb-4">
                    <div v-if="book.assembled.layout" class="text--secondary mb-2">
                      {{ $t('Text block') }}: {{ book.assembled.layout.textPages }}
                      {{ $tc('page', book.assembled.layout.textPages) }},
                      {{ book.assembled.layout.colorPages || 0 }} {{ $t('in colour') }}
                    </div>
                    <v-tabs v-model="book.reviewTab" dense show-arrows>
                      <v-tab v-for="file in reviewFiles(book)" :key="file.filename">
                        <v-icon small left>{{ fileIcon(file.filename) }}</v-icon>
                        {{ $t(file.label) }}
                      </v-tab>
                    </v-tabs>
                    <v-tabs-items v-model="book.reviewTab" class="review-pane">
                      <v-tab-item v-for="file in reviewFiles(book)" :key="file.filename">
                        <div class="d-flex align-center px-2 py-1 text--secondary review-bar">
                          <span class="text-truncate">{{ file.filename }}</span>
                          <v-spacer></v-spacer>
                          <v-btn icon small :href="file.url" target="_blank" :title="$t('Open in new tab')">
                            <v-icon small>mdi-open-in-new</v-icon>
                          </v-btn>
                        </div>
                        <iframe
                          v-if="file.kind === 'pdf'"
                          :src="reviewUrl(book, file)"
                          class="review-frame"
                          :title="file.filename"
                        ></iframe>
                        <pre v-else-if="book.xml[file.filename]" class="review-xml">{{
                          book.xml[file.filename]
                        }}</pre>
                        <div v-else-if="book.xml[file.filename] === null" class="pa-4 text--secondary">
                          {{ $t('Could not load') }} ·
                          <a :href="file.url" target="_blank">{{ file.filename }}</a>
                        </div>
                        <div v-else class="pa-4 text-center">
                          <v-progress-circular indeterminate size="20" width="2" />
                        </div>
                      </v-tab-item>
                    </v-tabs-items>
                  </div>

                  <v-alert v-if="book.problems.length" type="error" dense text>
                    <div class="font-weight-medium mb-1">{{ $t('CPI would refuse this book') }}</div>
                    <ul class="pl-4">
                      <li v-for="problem in book.problems" :key="problem">{{ problem }}</li>
                    </ul>
                  </v-alert>
                  <v-alert v-if="book.submission" type="success" dense text>
                    {{ $t('Submitted') }} · {{ book.submission.order }}
                    <span v-if="book.earlierSubmissions.length">
                      · {{ $t('{0} earlier CPI order(s)', [book.earlierSubmissions.length]) }}
                    </span>
                  </v-alert>
                  <v-btn
                    :color="book.submission ? undefined : 'error'"
                    :outlined="!!book.submission"
                    :disabled="!book.assembled || busy"
                    :loading="book.submitting"
                    @click="submit(book)"
                  >
                    <v-icon left>mdi-printer-wireless</v-icon>
                    {{ book.submission ? $t('Submit to CPI again') : $t('Submit to CPI') }}
                  </v-btn>
                  </template>
                </v-stepper-content>
              </v-stepper>
            </v-card>

            <div v-if="!loading && !books.length && !loadError" class="text--secondary pa-4">
              {{ $t('No horoscope book on this order') }}
            </div>
          </v-col>
        </v-row>
      </v-card>

      <v-snackbar v-model="snackbar.show" :color="snackbar.color" top :timeout="4000">
        {{ snackbar.message }}
      </v-snackbar>
    </v-dialog>
  </v-row>
</template>

<script>
  import moment from 'moment'
  import {
    isPdfFile,
    loadSource,
    renderThumbnail,
    destroySource,
  } from './PdfMerge/pdfMergeEngine'
  import {
    getHoroscopeBooks,
    updateHoroscopeBook,
    uploadHoroscopeContent,
    assembleHoroscopeBook,
    submitHoroscopeBook,
    sendHoroscopeBook,
  } from '../services/pdfGenerationService'
  import BirthPlaceField from './BirthPlaceField.vue'
  import HoroscopeGenerationPanel from './HoroscopeGenerationPanel.vue'
  import { hasCoordinates, placePayload, toPlace } from './birthPlace'
  import {
    GENDERS,
    bookGenders,
    bookTypeName,
    genderName,
    isPartnerBook,
    languageName,
    needsPolling,
  } from './horoscopeGeneration'

  /** While a text job or the background assembly runs: refresh this often, for at most this long. */
  const POLL_INTERVAL_MS = 10 * 1000
  const POLL_MAX_MS = 20 * 60 * 1000

  /**
   * One place for the manual CPI flow: staff read the horoscope data, generate
   * the text in their tool, drop the PDF here, and rest-api does the rest —
   * assembly through pdf-customizer, validation, mastering, order.
   */
  export default {
    name: 'HoroscopeBookDialog',
    components: { BirthPlaceField, HoroscopeGenerationPanel },
    props: {
      order: { type: Object, required: true },
      store: { type: Object, required: true },
      /** Track to open on: 'print' or 'digital'. Falls back to what the order bought. */
      track: { type: String, default: null },
    },
    data() {
      return {
        dialog: false,
        loading: false,
        loadError: false,
        books: [],
        designs: [],
        bookTypes: [],
        languages: [],
        snackbar: { show: false, color: 'primary', message: '' },
        pollTimer: null,
        pollUntil: 0,
      }
    },
    computed: {
      /** A classic design is bound to its type; rest-api keeps it whatever is chosen here. */
      bookTypeItems() {
        return this.bookTypes.map((type) => ({ value: type, text: this.$t(bookTypeName(type)) }))
      },
      /** A subject's gender: gravur picks the pronouns by it (in a Kinder book the child's). */
      genderItems() {
        return GENDERS.map((gender) => ({ value: gender, text: this.$t(genderName(gender)) }))
      },
      busy() {
        return (
          this.loading ||
          this.books.some((b) => b.uploading || b.assembling || b.submitting || b.sending)
        )
      },
      /** The legacy button's behaviour, kept as a fallback during proofing. */
      fallbackLabel() {
        return this.track === 'digital' ? 'Open digital PDF' : 'Download print files'
      },
      customerFields() {
        const c = this.order.customer || {}
        const a = this.order.shippingAddress
        return [
          { label: 'Name', value: [c.firstName, c.lastName].filter(Boolean).join(' ') },
          { label: 'Email', value: c.email },
          {
            label: 'Shipping address',
            value: a && [a.address1, a.zip, a.city, a.countryCode].filter(Boolean).join(', '),
          },
        ].filter((f) => f.value)
      },
    },
    watch: {
      dialog(open) {
        if (open) this.load()
        else this.reset()
      },
    },
    beforeDestroy() {
      this.reset()
    },
    methods: {
      notify(message, color = 'primary') {
        this.snackbar = { show: true, message, color }
      },
      /** Opens the dialog without its activator (a page that just created the book). */
      open() {
        this.dialog = true
      },
      async load() {
        this.loading = true
        this.loadError = false
        try {
          const { data } = await getHoroscopeBooks(this.order.id, this.store.id)
          this.designs = data.designs || []
          this.bookTypes = data.bookTypes || []
          this.languages = data.languages || []
          this.books = (data.books || []).map((book) => ({
            ...book,
            // Each subject's gender ({ native, partner? }); declared so Vue 2 tracks it.
            genders: book.genders || null,
            // Inline editing of the horoscope data (see infoFields / saveEdit).
            editing: null,
            draft: {},
            saving: false,
            // Every stage key declared up front so Vue 2 tracks them.
            file: null,
            source: null,
            thumb: null,
            pageCount: 0,
            dragOver: false,
            uploading: false,
            uploaded: book.content || null,
            content: book.content || null,
            // Generated text (gravur) and the last failed automated step.
            generation: book.generation || null,
            lastError: book.lastError || null,
            assembling: false,
            // One assembly per version from the server; `assembled` is the current track's.
            assemblies: book.assemblies || {},
            assembled: null,
            submitting: false,
            submission: book.submission || null,
            earlierSubmissions: book.earlierSubmissions || [],
            // Digital delivery (backend to follow): the email and what was sent.
            // Tracks come from the book's _package; open on the requested one when it has it.
            tracks: book.tracks && book.tracks.length ? book.tracks : ['print', 'digital'],
            track: this.initialTrackFor(book),
            emailTo: (this.order.customer && this.order.customer.email) || '',
            sending: false,
            delivery: book.delivery || null,
            problems: [],
            // Open stepper step; completed steps stay clickable to redo them.
            step: 1,
            // Review panel: selected tab and the fetched XML texts by filename.
            reviewTab: 0,
            xml: {},
          }))
          this.books.forEach((book) => {
            this.syncAssembled(book)
            book.step = this.stage(book)
            this.loadXml(book)
          })
          this.startPolling()
        } catch (error) {
          console.error(error)
          this.loadError = true
        } finally {
          this.loading = false
        }
      },
      reset() {
        this.stopPolling()
        this.books.forEach((b) => this.dropFile(b))
        this.books = []
        this.loadError = false
      },

      // ---- horoscope info ----------------------------------------------------
      /**
       * Rows of the left panel. `key` is the override name the backend knows;
       * `edited` marks a value that differs from the order because staff changed it;
       * `noCoordinates` a birthplace without them (no wheel chart, no generated text).
       */
      infoFields(book) {
        const p = book.properties || {}
        const o = book.overrides || {}
        const edited = (...keys) => keys.some((k) => k in o)
        const genders = bookGenders(book)
        const fields = [
          { key: 'variant', label: 'Cover', type: 'design', value: book.variant, edited: edited('variant') },
          {
            key: 'bookType',
            label: 'Book type',
            type: 'book-type',
            value: this.$t(bookTypeName(book.bookType)),
            edited: edited('bookType'),
          },
          {
            key: 'language',
            label: 'Language',
            type: 'language',
            value: this.languageLabel(book.language),
            edited: edited('language'),
          },
          { key: 'name', label: 'Name', type: 'text', value: p.name, edited: edited('name') },
          {
            key: 'gender',
            label: 'Gender',
            type: 'gender',
            value: this.$t(genderName(genders.native)),
            edited: edited('gender'),
          },
          {
            key: 'birthDate',
            label: 'Birth date',
            type: 'date',
            value: this.formatBirth(p.birthDate, p.dateFormat, p.showTime),
            edited: edited('birthDate', 'showTime'),
          },
          {
            key: 'birthPlace',
            label: 'Birth place',
            type: 'place',
            value: this.formatPlace(p.birthPlace),
            edited: edited('birthPlace'),
            noCoordinates: !!p.birthPlace && !hasCoordinates(p.birthPlace),
          },
        ]
        if (isPartnerBook(book)) {
          fields.push(
            { key: 'namePartner', label: 'Partner name', type: 'text', value: p.namePartner, edited: edited('namePartner') },
            {
              key: 'genderPartner',
              label: 'Partner gender',
              type: 'gender',
              value: this.$t(genderName(genders.partner)),
              edited: edited('genderPartner'),
            },
            {
              key: 'birthDatePartner',
              label: 'Partner birth date',
              type: 'date',
              value: this.formatBirth(p.birthDatePartner, p.dateFormat, p.showTime2),
              edited: edited('birthDatePartner', 'showTime2'),
            },
            {
              key: 'birthPlacePartner',
              label: 'Partner birth place',
              type: 'place',
              value: this.formatPlace(p.birthPlacePartner),
              edited: edited('birthPlacePartner'),
              noCoordinates: !!p.birthPlacePartner && !hasCoordinates(p.birthPlacePartner),
            }
          )
        }
        fields.push({
          key: 'greetingMessage',
          label: 'Greeting message',
          type: 'text-long',
          value: p.greetingMessage,
          edited: edited('greetingMessage'),
        })
        return fields
      },

      // ---- inline edits (overrides on the book, applied at the next assembly) --------
      startEdit(book, field) {
        const p = book.properties || {}
        if (field.type === 'date') {
          const partner = field.key === 'birthDatePartner'
          const raw = partner ? p.birthDatePartner : p.birthDate
          const date = raw !== undefined && raw !== null && raw !== '' ? moment.unix(Number(raw)).utc() : null
          book.draft = {
            date: date && date.isValid() ? date.format('DD.MM.YYYY') : '',
            time: date && date.isValid() ? date.format('HH:mm') : '',
            showTime: Boolean(partner ? p.showTime2 : p.showTime),
          }
        } else if (field.type === 'design') {
          book.draft = { value: book.variant }
        } else if (field.type === 'book-type') {
          book.draft = { value: book.bookType }
        } else if (field.type === 'language') {
          book.draft = { value: book.language }
        } else if (field.type === 'gender') {
          const genders = bookGenders(book)
          book.draft = { value: field.key === 'genderPartner' ? genders.partner : genders.native }
        } else if (field.type === 'place') {
          book.draft = { place: toPlace(p[field.key]) }
        } else {
          book.draft = { value: field.value || '' }
        }
        book.editing = field.key
      },
      /** The override payload for a field's draft, or an error message. */
      draftOverrides(book, field) {
        const d = book.draft
        if (field.type === 'date') {
          const time = d.showTime ? d.time || '00:00' : '00:00'
          const parsed = moment.utc(`${d.date} ${time}`, 'DD.MM.YYYY HH:mm', true)
          if (!parsed.isValid()) return { error: this.$t('Enter the date as DD.MM.YYYY and the time as HH:mm') }
          const partner = field.key === 'birthDatePartner'
          return {
            overrides: partner
              ? { birthDatePartner: String(parsed.unix()), showTime2: Boolean(d.showTime) }
              : { birthDate: String(parsed.unix()), showTime: Boolean(d.showTime) },
          }
        }
        if (field.type === 'place') {
          const place = placePayload(d.place)
          return place ? { overrides: { [field.key]: place } } : { error: this.$t('A value is required') }
        }
        const value = (d.value || '').trim()
        if (field.type !== 'text-long' && !value) return { error: this.$t('A value is required') }
        return { overrides: { [field.key]: value } }
      },
      async saveEdit(book, field) {
        const { overrides, error } = this.draftOverrides(book, field)
        if (error) return this.notify(error, 'warning')
        await this.patch(book, overrides)
      },
      async revertEdit(book, field) {
        const keys =
          field.key === 'birthDate'
            ? ['birthDate', 'showTime']
            : field.key === 'birthDatePartner'
              ? ['birthDatePartner', 'showTime2']
              : [field.key]
        await this.patch(book, Object.fromEntries(keys.map((k) => [k, null])))
      },
      async patch(book, overrides) {
        book.saving = true
        try {
          const { data } = await updateHoroscopeBook(this.order.id, book.lineItemId, this.store.id, overrides)
          this.applyView(book, data)
          book.editing = null
          book.problems = []
          this.notify(this.$t('Saved — assemble again to apply'), 'success')
        } catch (error) {
          this.notify(this.errorMessage(error, 'Could not save'), 'error')
        } finally {
          book.saving = false
        }
      },
      formatBirth(unixSeconds, dateFormat, showTime) {
        if (unixSeconds === undefined || unixSeconds === null || unixSeconds === '') return ''
        const date = moment.unix(Number(unixSeconds)).utc()
        if (!date.isValid()) return String(unixSeconds)
        let format = dateFormat || 'DD.MM.YYYY'
        if (showTime && !format.includes('HH:mm')) format = `${format}, HH:mm`
        return date.format(format)
      },
      languageLabel(code) {
        return code ? `${this.$t(languageName(code))} (${code.toUpperCase()})` : ''
      },
      /** The languages a book can be made in; a store language outside them stays visible as the current value. */
      languageItems(book) {
        const codes = this.languages.includes(book.language) ? this.languages : [...this.languages, book.language]
        return codes.filter(Boolean).map((code) => ({
          value: code,
          text: this.languageLabel(code),
          disabled: !this.languages.includes(code),
        }))
      },
      formatPlace(place) {
        if (!place) return ''
        if (typeof place === 'string') return place
        return place.fullAddress || place.name || place.address || ''
      },
      async copy(text) {
        try {
          await navigator.clipboard.writeText(String(text))
          this.notify(this.$t('Copied'))
        } catch (error) {
          this.notify(this.$t('Could not copy'), 'error')
        }
      },

      // ---- stage 1: text PDF ------------------------------------------------------
      openFileDialog(book) {
        const [input] = this.$refs[`file-${book.lineItemId}`] || []
        if (!input) return
        input.value = ''
        input.click()
      },
      onFileInput(book, e) {
        this.setFile(book, e.target.files[0])
      },
      onDrop(book, e) {
        book.dragOver = false
        this.setFile(book, e.dataTransfer.files[0])
      },
      async setFile(book, file) {
        if (!file) return
        if (!isPdfFile(file)) {
          this.notify(this.$t('Only PDF files are accepted'), 'warning')
          return
        }
        this.dropFile(book)
        book.file = file
        try {
          const { source } = await loadSource(file)
          book.source = source
          book.pageCount = source.pageCount
          book.thumb = await renderThumbnail(source, 0)
        } catch (error) {
          console.error(error)
          this.notify(this.$t('Could not read the PDF'), 'error')
          this.dropFile(book)
        }
      },
      dropFile(book) {
        if (book.thumb) URL.revokeObjectURL(book.thumb)
        if (book.source) destroySource(book.source)
        book.file = null
        book.source = null
        book.thumb = null
        book.pageCount = 0
      },
      async upload(book) {
        book.uploading = true
        try {
          const { data } = await uploadHoroscopeContent(
            this.order.id,
            book.lineItemId,
            book.file,
            this.store.id
          )
          // The server drops anything assembled from the previous text.
          this.applyView(book, data)
          book.problems = []
          this.notify(this.$t('Text uploaded'), 'success')
        } catch (error) {
          this.notify(this.errorMessage(error, 'Upload failed'), 'error')
        } finally {
          book.uploading = false
        }
      },

      // ---- stage 2: assemble ------------------------------------------------------
      async assemble(book) {
        book.assembling = true
        book.problems = []
        try {
          const { data } = await assembleHoroscopeBook(
            this.order.id,
            book.lineItemId,
            this.store.id,
            book.track
          )
          this.applyView(book, data)
          this.notify(this.$t('Book assembled'), 'success')
        } catch (error) {
          this.notify(this.errorMessage(error, 'Assembly failed'), 'error')
        } finally {
          book.assembling = false
        }
      },

      // ---- stage 3: submit --------------------------------------------------------
      async submit(book) {
        const resubmit = !!book.submission
        const question = resubmit
          ? this.$t('This book was already ordered at CPI ({0}). Place ANOTHER real print order? CPI may set aside a second order under the same number as a duplicate.', [book.submission.order])
          : this.$t('Place a real print order with CPI for this book?')
        if (!window.confirm(question)) return
        book.submitting = true
        book.problems = []
        try {
          const { data } = await submitHoroscopeBook(this.order.id, book.lineItemId, this.store.id, { resubmit })
          this.applyView(book, data)
          this.notify(
            this.$t(data.status === 'exists' ? 'Already submitted to CPI' : 'Submitted to CPI'),
            'success'
          )
        } catch (error) {
          const problems = error.response && error.response.data && error.response.data.problems
          if (Array.isArray(problems) && problems.length) book.problems = problems
          else this.notify(this.errorMessage(error, 'Submission failed'), 'error')
        } finally {
          book.submitting = false
        }
      },

      // ---- digital: send to customer (backend to follow) -------------------------------
      isValidEmail(value) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value || '').trim())
      },
      async send(book) {
        book.sending = true
        try {
          const { data } = await sendHoroscopeBook(this.order.id, book.lineItemId, this.store.id, {
            to: book.emailTo.trim(),
          })
          this.applyView(book, data)
          this.notify(this.$t('Sent to customer'), 'success')
        } catch (error) {
          const status = error.response && error.response.status
          this.notify(
            status === 404
              ? this.$t('Digital delivery is not connected yet')
              : this.errorMessage(error, 'Sending failed'),
            status === 404 ? 'warning' : 'error'
          )
        } finally {
          book.sending = false
        }
      },
      /** Each track has its own assembly on the server; switching shows that one. */
      onTrackChange(book) {
        this.syncAssembled(book)
        book.problems = []
        book.reviewTab = 0
        book.step = this.stage(book)
        this.loadXml(book)
      },
      syncAssembled(book) {
        book.assembled = (book.assemblies && book.assemblies[book.track]) || null
      },
      initialTrackFor(book) {
        const tracks = book.tracks && book.tracks.length ? book.tracks : ['print', 'digital']
        return tracks.includes(this.track) ? this.track : tracks[0]
      },
      isDelivered(book) {
        return book.track === 'digital' ? !!book.delivery : !!book.submission
      },
      /** Ordered at CPI: everything stays editable, the print track warns that CPI has the earlier version. */
      isAtCpi(book) {
        return book.track === 'print' && !!book.submission
      },

      /** Every book route answers with the book view; the data and stage fields come from it. */
      applyView(book, view) {
        book.variant = view.variant
        book.bookType = view.bookType || null
        book.language = view.language || null
        book.genders = view.genders || null
        book.properties = view.properties
        book.original = view.original || null
        book.overrides = view.overrides || null
        book.editedAt = view.editedAt || null
        book.editedBy = view.editedBy || null
        book.uploaded = view.content || null
        book.content = view.content || null
        book.generation = view.generation || null
        book.lastError = view.lastError || null
        book.assemblies = view.assemblies || {}
        this.syncAssembled(book)
        book.submission = view.submission || null
        book.earlierSubmissions = view.earlierSubmissions || []
        book.delivery = view.delivery || null
        book.step = this.stage(book)
        book.reviewTab = 0
        book.xml = {}
        this.loadXml(book)
      },

      // ---- generated text: refresh while gravur or the background assembly works ----------
      onGenerationView(book, view) {
        this.applyView(book, view)
        this.startPolling()
      },
      startPolling() {
        this.pollUntil = Date.now() + POLL_MAX_MS
        this.schedulePoll()
      },
      schedulePoll() {
        if (this.pollTimer || !this.dialog || Date.now() > this.pollUntil) return
        if (!this.books.some(needsPolling)) return
        this.pollTimer = setTimeout(this.poll, POLL_INTERVAL_MS)
      },
      stopPolling() {
        clearTimeout(this.pollTimer)
        this.pollTimer = null
      },
      /** Re-reads the order's books; a book changes only when its server state did and staff are not mid-step. */
      async poll() {
        this.stopPolling()
        if (!this.dialog) return
        try {
          const { data } = await getHoroscopeBooks(this.order.id, this.store.id)
          if (!this.dialog) return
          for (const view of data.books || []) {
            const book = this.books.find((b) => b.lineItemId === view.lineItemId)
            if (!book || book.editing || book.saving || book.uploading || book.assembling) continue
            if (book.submitting || book.sending) continue
            if (this.serverState(book) !== this.serverState(view)) this.applyView(book, view)
          }
        } catch (error) {
          console.error(error)
        }
        this.schedulePoll()
      },
      serverState(view) {
        const { generation, content, assemblies, submission, delivery, lastError } = view
        return JSON.stringify({ generation, content, assemblies, submission, delivery, lastError })
      },

      // ---- review -------------------------------------------------------------------
      /** The files CPI receives, in review order: cover, text block, title XML, order XML. */
      reviewFiles(book) {
        const files = (book.assembled && book.assembled.files) || []
        const find = (test) => files.find((f) => test(f.filename))
        return [
          { label: 'Cover', kind: 'pdf', file: find((n) => n.endsWith('_cover.pdf')) },
          { label: 'Text block', kind: 'pdf', file: find((n) => n.endsWith('_text.pdf')) },
          { label: 'Mastering XML', kind: 'xml', file: find((n) => n.endsWith('_MasteringOrder.xml')) },
          { label: 'Order XML', kind: 'xml', file: find((n) => n.startsWith('CPI_DropShipOrder_')) },
        ]
          .filter((entry) => entry.file)
          .map(({ label, kind, file }) => ({ label, kind, ...file }))
      },
      /**
       * Served file names are stable across re-assemblies, so the browser would
       * happily show last time's PDF; the assembly time makes each one a new URL.
       */
      reviewUrl(book, file) {
        const at = book.assembled && book.assembled.at
        const version = at ? new Date(at).getTime() : Date.now()
        return `${file.url}${file.url.includes('?') ? '&' : '?'}v=${version}`
      },
      /** XML is small text; fetch it once per assembly so staff read it in place. */
      async loadXml(book) {
        for (const file of this.reviewFiles(book).filter((f) => f.kind === 'xml')) {
          try {
            const response = await fetch(this.reviewUrl(book, file), { cache: 'no-store' })
            if (!response.ok) throw new Error(`HTTP ${response.status}`)
            this.$set(book.xml, file.filename, await response.text())
          } catch (error) {
            console.error(error)
            this.$set(book.xml, file.filename, null)
          }
        }
      },

      // ---- presentation -----------------------------------------------------------
      stage(book) {
        if (this.isDelivered(book) || book.assembled) return 3
        if (book.uploaded) return 2
        return 1
      },
      stageLabel(book) {
        if (this.isDelivered(book)) return book.track === 'digital' ? 'Sent' : 'Submitted'
        if (book.assembled) return book.track === 'digital' ? 'Ready to send' : 'Ready to submit'
        if (book.uploaded) return book.uploaded.source === 'gravur' ? 'Text generated' : 'Text uploaded'
        return 'Waiting for text'
      },
      stageColor(book) {
        if (this.isDelivered(book)) return 'success'
        if (book.assembled) return 'primary'
        if (book.uploaded) return 'secondary'
        return 'grey'
      },
      fileIcon(filename = '') {
        return /\.xml$/i.test(filename) ? 'mdi-xml' : 'mdi-file-pdf-box'
      },
      formatSize(bytes) {
        if (!bytes) return '0 B'
        const units = ['B', 'KB', 'MB', 'GB']
        const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)))
        return `${(bytes / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${units[i]}`
      },
      errorMessage(error, fallback) {
        const upstream = error.response && error.response.data && error.response.data.message
        return upstream || this.$t(fallback)
      },
    },
  }
</script>

<style scoped>
  .book-dropzone {
    border: 2px dashed rgba(255, 255, 255, 0.3);
    border-radius: 12px;
    padding: 16px 20px;
    cursor: pointer;
    transition: border-color 0.2s, background-color 0.2s;
  }
  .book-dropzone:hover,
  .book-dropzone--over {
    border-color: #64b5f6;
    background: rgba(100, 181, 246, 0.08);
  }
  .book-thumb {
    width: 56px;
    height: 79px;
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff;
    border-radius: 3px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.45);
    overflow: hidden;
  }
  .book-thumb img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }

  .info-column .v-list-item__content {
    text-align: left;
  }

  .review-pane {
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 0 0 8px 8px;
    background: rgba(0, 0, 0, 0.2);
  }
  .review-bar {
    font-size: 0.8rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }
  .review-frame {
    display: block;
    width: 100%;
    height: 70vh;
    border: 0;
    background: #525659;
  }
  .review-xml {
    margin: 0;
    padding: 12px 16px;
    max-height: 70vh;
    overflow: auto;
    font-size: 0.8rem;
    line-height: 1.45;
    white-space: pre;
    text-align: left;
    tab-size: 4;
  }
</style>
